import React from 'react';
import { lowerCase } from 'lodash';
import { Tab, Tabs } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader } from '@progress/kendo-react-indicators';

import {
  useGetEventReportHeader,
  useGetEventReportGameTab
} from '../hooks/services/use-reports';
import { formatDateToDDMMYYYY } from '../utils/date';
import { InfoText } from '../features/report/info-text';
import { PrivatePage } from '../components/private-page';
import { GameActivityReport } from '../features/report/game';
import { TasksReportTab } from '../features/report/game/tabs/tasks';
import { TeamsReportTab } from '../features/report/game/tabs/teams';
import { FeedbackActivityReport } from '../features/report/feedback';
import { GroupsReportTab } from '../features/report/game/tabs/groups';
import { PlayersReportTab } from '../features/report/game/tabs/players';
import { AssessmentActivityReport } from '../features/report/assessment';
import { FeedbackResponsesTab } from '../features/report/feedback/tabs/responses';
import { ActionPlanReportTab } from '../features/report/assessment/tabs/action-plan';
import { MeasurableReportTab } from '../features/report/assessment/tabs/measurables';
import { RatingCompetenciesReportTab } from '../features/report/assessment/tabs/rating-competencies';
import { ParticipantCompetenciesReportTab } from '../features/report/assessment/tabs/participant-competencies';

export const formatTime = (time: number) => {
  if (time !== undefined) {
    let hours = Math.floor(time / 60);
    let minutes = Math.round(time % 60);

    // if hours or minutes are greater than 1 return text in plural
    let hText = hours > 1 ? 'hours' : 'hour';
    let mText = minutes > 1 ? 'minutes' : 'minute';

    // show hour if hours > 0
    let hour = hours > 0 ? hours + hText : '';

    return hour + ' ' + minutes + mText;
  }
};

const UrlText: React.FC<{
  heading: string;
  textGroup: { id: number; name: string }[];
  type: 'assessment' | 'feedback' | 'game';
}> = ({ heading, textGroup, type }) => {
  const { eventId, detailId, type: parentType } = useParams();
  const navigate = useNavigate();

  return (
    <div className={'d-flex flex-column'}>
      <span
        style={{
          color: '#A5B7BF'
        }}>
        {heading}
      </span>
      <div className={'d-flex'}>
        {textGroup.map((text, idx) => {
          const isLast = idx === textGroup.length - 1;
          const label = isLast ? text.name : `${text.name}, `;
          return type !== parentType || text.id !== parseInt(detailId ?? '') ? (
            <span
              key={text.id}
              className={
                'link-primary cursor-pointer p-0 fw-bold text-decoration-none'
              }
              onClick={() =>
                navigate(`/reports/${eventId}/${text.id}/${lowerCase(type)}`)
              }>
              {label}
            </span>
          ) : (
            <span key={text.id} className={'fw-bold'}>
              {label}
            </span>
          );
        })}
      </div>
    </div>
  );
};

const Report = () => {
  const { eventId, detailId, type } = useParams();

  const { data: header, isLoading } = useGetEventReportHeader(eventId);

  const activities = React.useMemo(() => {
    if (!header) return [];
    return header.activities;
  }, [header]);

  const assessments = React.useMemo(() => {
    if (!header) return [];
    return header.assessments;
  }, [header]);

  const feedbacks = React.useMemo(() => {
    if (!header) return [];
    return header.feedbacks;
  }, [header]);

  const { data: teams } = useGetEventReportGameTab({
    tab: 'teams',
    eventId: activities.length > 0 ? eventId : undefined,
    playlistId: detailId
  });

  const { data: groups } = useGetEventReportGameTab({
    tab: 'groups',
    eventId: activities.length > 0 ? eventId : undefined,
    playlistId: detailId
  });

  return (
    <PrivatePage
      className={'page-base--full detail-report'}
      breadcrumb={[
        {
          label: 'Reports',
          href: 'reports'
        },
        {
          label: `${header?.name}`,
          href: `reports/${eventId}/${detailId}/${type}`
        }
      ]}
      pageTitle={'Reports'}>
      {(isLoading || !header) && (
        <div
          className={
            'd-flex flex-column align-items-center gap-2 text-muted w-100 h-100'
          }>
          <Loader type={'pulsing'} />
          <span>Loading data</span>
        </div>
      )}

      {header && (
        <Tabs
          defaultActiveKey={'overview'}
          className={'mb-3'}
          mountOnEnter
          unmountOnExit>
          <Tab eventKey={'overview'} title={'Overview'}>
            <div className={'d-flex flex-column gap-2 w-100 overflow-auto'}>
              <div
                style={{
                  backgroundColor: '#F8FCFE',
                  border: '1px solid #CFD4D9',
                  borderRadius: '4px',
                  padding: '22px 27px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '17px'
                }}>
                <span className={'fw-bold'}>Event details</span>
                <div
                  className={'d-flex'}
                  style={{
                    gap: '92px'
                  }}>
                  <InfoText heading={'Event name'} text={header?.name ?? ''} />
                  <InfoText
                    heading={'Date'}
                    text={`${formatDateToDDMMYYYY(
                      header?.actualStartDateUtc ?? '',
                      true
                    )} - ${formatDateToDDMMYYYY(
                      header?.actualEndDateUtc ?? '',
                      true
                    )}`}
                  />
                  <InfoText
                    heading={'Duration'}
                    text={formatTime(header?.duration ?? 0) ?? ''}
                  />
                  <InfoText heading={'Pax'} text={header?.pax ?? ''} />
                </div>
                {activities.length > 0 && (
                  <UrlText
                    heading={'Activities'}
                    textGroup={activities}
                    type={'game'}
                  />
                )}
                {assessments.length + feedbacks.length > 0 && (
                  <div
                    className={'d-flex'}
                    style={{
                      gap: '92px'
                    }}>
                    {assessments.length > 0 && (
                      <UrlText
                        heading={'Assessment'}
                        textGroup={assessments}
                        type={'assessment'}
                      />
                    )}
                    {feedbacks.length > 0 && (
                      <UrlText
                        heading={'Feedback'}
                        textGroup={feedbacks}
                        type={'feedback'}
                      />
                    )}
                  </div>
                )}
              </div>

              {type && type === 'game' && (
                <GameActivityReport eventName={header.name} />
              )}
              {type && type === 'assessment' && (
                <AssessmentActivityReport eventName={header.name} />
              )}
              {type && type === 'feedback' && (
                <FeedbackActivityReport eventName={header.name} />
              )}
            </div>
          </Tab>

          {/* game tabs */}
          {type && type === 'game' && (
            <Tab eventKey={'tasks'} title={'Tasks'}>
              <TasksReportTab headerReport={header} />
            </Tab>
          )}

          {type && type === 'game' && (
            <Tab eventKey={'players'} title={'Players'}>
              <PlayersReportTab headerReport={header} />
            </Tab>
          )}

          {type && type === 'game' && teams && teams.data.length > 0 && (
            <Tab eventKey={'teams'} title={'Teams'}>
              <TeamsReportTab headerReport={header} />
            </Tab>
          )}

          {type && type === 'game' && groups && groups.data.length > 0 && (
            <Tab eventKey={'groups'} title={'Groups'}>
              <GroupsReportTab headerReport={header} />
            </Tab>
          )}
          {/* end game tabs */}

          {/* assessment tabs */}
          {type && type === 'assessment' && (
            <Tab eventKey={'competency'} title={'Competency Rating'}>
              <ParticipantCompetenciesReportTab />
            </Tab>
          )}

          {type && type === 'assessment' && (
            <Tab
              eventKey={'rating-summary'}
              title={'Competency Ratings Summary'}>
              <RatingCompetenciesReportTab headerReport={header} />
            </Tab>
          )}

          {type && type === 'assessment' && (
            <Tab eventKey={'action-plan'} title={'Action Plan'}>
              <ActionPlanReportTab />
            </Tab>
          )}

          {type && type === 'assessment' && (
            <Tab eventKey={'measurables'} title={'Measurables'}>
              <MeasurableReportTab />
            </Tab>
          )}
          {/* end assessment tabs */}

          {/* feedback tabs */}
          {type && type === 'feedback' && (
            <Tab eventKey={'response'} title={'Responses'}>
              <FeedbackResponsesTab />
            </Tab>
          )}
          {/* end feedback tabs */}
        </Tabs>
      )}
    </PrivatePage>
  );
};

export default Report;
